import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  Grid,
  Typography,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Fab,
  Autocomplete,
  Chip
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useSkillSheetMainStepper } from '../context/SkillSheetMainStepperContext';
import { useRestoreChildOptions } from '../hooks/useRestoreChildOptions';

export default function BusinessSystemForm({ isEdit = false }) {
  // コンテキストの取得を条件分岐で行う
  let stepperContext;

  if (isEdit) {
    try {
      stepperContext = useSkillSheetMainStepper();
    } catch (error) {
      console.error('編集用コンテキストの取得に失敗しました:', error);
      throw error;
    }
  } else {
    try {
      stepperContext = useSkillSheetMainStepper();
    } catch (error) {
      console.error('作成用コンテキストの取得に失敗しました:', error);
      throw error;
    }
  }

  const {
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
    fetchMasterData,
    fetchBusinessSystemProductLanguageData,
    masterData,
    validateStep
  } = stepperContext;

  // バリデーション表示の状態
  const [showValidation, setShowValidation] = useState(false);
  // 削除確認ダイアログの状態
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, index: null });
  // 新しく追加された要素へのスクロール用ref
  const formRefs = useRef([]);
  // 各カテゴリーの言語選択肢を保持
  const [languageOptions, setLanguageOptions] = useState({});
  // 各カテゴリーの言語入力値を保持
  const [languageInputValues, setLanguageInputValues] = useState({});

  // フォームの値を監視
  const business_systems = watch('business_systems') || [{ business_system_name: '', business_system_id: '', business_system_product_language_name: '', business_system_product_language_id: '' }];

  // 既に選択済みのカテゴリーを取得する関数
  const getSelectedBusinessSystems = useCallback(() => {
    return business_systems
      .map(system => system.business_system_name)
      .filter(name => name && name.trim() !== '');
  }, [business_systems]);

  // 既に選択済みの言語を取得する関数
  const getSelectedLanguages = useCallback((currentIndex) => {
    const allLanguages = [];
    business_systems.forEach((system, index) => {
      if (index !== currentIndex && system.business_system_product_language_name) {
        const languages = system.business_system_product_language_name.split(', ').filter(f => f.trim() !== '');
        allLanguages.push(...languages);
      }
    });
    return allLanguages;
  }, [business_systems]);

  // カテゴリーの選択肢をフィルタリングする関数
  const getFilteredBusinessSystemOptions = useCallback((currentIndex) => {
    const selectedSystems = getSelectedBusinessSystems();
    const currentSystem = business_systems[currentIndex]?.business_system_name;

    return (masterData.business_systems || []).filter(system => {
      const systemName = typeof system === 'string' ? system : system.name;
      return !selectedSystems.includes(systemName) || systemName === currentSystem;
    });
  }, [masterData.business_systems, getSelectedBusinessSystems, business_systems]);

  // 言語の選択肢をフィルタリングする関数
  const getFilteredLanguageOptions = useCallback((currentIndex) => {
    const selectedLanguages = getSelectedLanguages(currentIndex); // 他のフォームで選択済み
    const currentLanguages = business_systems[currentIndex]?.business_system_product_language_name 
      ? business_systems[currentIndex].business_system_product_language_name.split(', ').filter(f => f.trim() !== '')
      : []; // 現在のフォームで選択済み

    // 全ての選択済み言語を結合
    const allSelectedLanguages = [...selectedLanguages, ...currentLanguages];

    return (languageOptions[business_systems[currentIndex]?.business_system_name] || []).filter(lang => {
      const languageName = typeof lang === 'string' ? lang : lang.name;
      return !allSelectedLanguages.includes(languageName);
    });
  }, [languageOptions, getSelectedLanguages, business_systems]);

  // マスタデータを取得
  useEffect(() => {
    fetchMasterData('business_systems');
  }, [fetchMasterData]);

  // ページ遷移などで再マウントされた際、既に業務システムが選択済みの行の製品・言語候補を復元する
  useRestoreChildOptions(
    business_systems,
    (system) => system.business_system_name,
    languageOptions,
    setLanguageOptions,
    fetchBusinessSystemProductLanguageData,
    masterData.business_systems || [],
  );

  // バリデーション関数
  const validateForm = () => {
    let hasError = false;
    clearErrors();

    // business_systemsのバリデーション
    business_systems.forEach((system, index) => {
      const hasBusinessSystemName = system.business_system_name && system.business_system_name !== '';
      const hasLanguageName = system.business_system_product_language_name && system.business_system_product_language_name !== '';

      // 言語が入力されている場合のみ、カテゴリーも必須にする
      // カテゴリーのみの場合はバリデーションしない
      if (hasLanguageName && !hasBusinessSystemName) {
        setError(`business_systems.${index}.business_system_name`, {
          type: 'required',
          message: 'カテゴリーを入力してください'
        });
        hasError = true;
      }
    });

    if (hasError) {
      setShowValidation(true);
      // エラーがある場合、最初のエラーフィールドまでスクロール
      setTimeout(() => {
        const firstErrorField = document.querySelector('[data-error="true"]');
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } else {
      setShowValidation(false);
    }

    return !hasError;
  };

  // バリデーションエラーが表示された時の処理
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      setShowValidation(true);
      // エラーがある場合、最初のエラーフィールドまでスクロール
      setTimeout(() => {
        const firstErrorField = document.querySelector('[data-error="true"]');
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } else {
      setShowValidation(false);
    }
  }, [errors]);

  // フォームバリデーションを外部から呼び出せるように登録
  useEffect(() => {
    if (validateStep) {
      validateStep(9, validateForm); // 10ステップ目（0ベースで9）
    }
  }, [validateStep, business_systems]);

  // カテゴリー選択の変更ハンドラ
  const handleBusinessSystemChange = async (index, selectedSystem) => {
    const newSystems = [...business_systems];

    if (typeof selectedSystem === 'string') {
      // 手動入力の場合
      newSystems[index] = {
        ...newSystems[index],
        business_system_name: selectedSystem,
        business_system_id: '',
        // カテゴリーが変更されたら言語をクリア
        business_system_product_language_name: '',
        business_system_product_language_id: ''
      };
    } else if (selectedSystem && selectedSystem.name) {
      // オートコンプリートから選択した場合
      newSystems[index] = {
        ...newSystems[index],
        business_system_name: selectedSystem.name,
        business_system_id: selectedSystem.id || '',
        // カテゴリーが変更されたら言語をクリア
        business_system_product_language_name: '',
        business_system_product_language_id: ''
      };

      // 選択したカテゴリーに基づいて言語の選択肢を取得（カテゴリー名をキーに保存）
      try {
        const languageData = await fetchBusinessSystemProductLanguageData(selectedSystem.name);
        setLanguageOptions(prev => ({
          ...prev,
          [selectedSystem.name]: languageData
        }));
      } catch (error) {
        console.error('Error fetching language options:', error);
      }
    } else if (selectedSystem === null || selectedSystem === '') {
      // クリアされた場合
      newSystems[index] = {
        ...newSystems[index],
        business_system_name: '',
        business_system_id: '',
        business_system_product_language_name: '',
        business_system_product_language_id: ''
      };
      // 言語の候補はカテゴリー名をキーに保持しているため、
      // カテゴリー名が空になれば getFilteredLanguageOptions 側で自然に空扱いになる
    }

    setValue('business_systems', newSystems, { shouldDirty: true, shouldTouch: true });

    // 言語の入力値もクリア
    setLanguageInputValues(prev => ({
      ...prev,
      [index]: ''
    }));

    // 変更時に該当フィールドのエラーをクリア
    const fieldErrors = errors?.business_systems;
    if (fieldErrors && fieldErrors[index]) {
      if (fieldErrors[index].business_system_name) {
        clearErrors(`business_systems.${index}.business_system_name`);
      }
      if (fieldErrors[index].business_system_product_language_name) {
        clearErrors(`business_systems.${index}.business_system_product_language_name`);
      }
    }
  };

  // 言語選択の変更ハンドラ
  const handleLanguageChange = (index, newValue) => {
    const newSystems = [...business_systems];

    // 選択された言語を文字列に変換
    const languageNames = newValue.map(item =>
      typeof item === 'string' ? item : item.name || ''
    ).join(', ');

    newSystems[index] = {
      ...newSystems[index],
      business_system_product_language_name: languageNames
    };
    setValue('business_systems', newSystems, { shouldDirty: true, shouldTouch: true });

    // 変更時に該当フィールドのエラーをクリア
    const fieldErrors = errors?.business_systems;
    if (fieldErrors && fieldErrors[index] && fieldErrors[index].business_system_product_language_name) {
      clearErrors(`business_systems.${index}.business_system_product_language_name`);
    }
  };

  // 言語入力値変更ハンドラ
  const handleLanguageInputChange = (index, newInputValue) => {
    setLanguageInputValues(prev => ({
      ...prev,
      [index]: newInputValue
    }));
  };

  // 言語の手動入力確定
  const handleLanguageBlur = (index) => {
    const inputValue = languageInputValues[index];
    if (inputValue && inputValue.trim() !== '') {
      const currentLanguages = business_systems[index].business_system_product_language_name
        ? business_systems[index].business_system_product_language_name.split(', ').filter(f => f.trim() !== '')
        : [];

      const trimmedInput = inputValue.trim();
      if (!currentLanguages.includes(trimmedInput)) {
        const newLanguages = [...currentLanguages, trimmedInput];
        const newSystems = [...business_systems];
        newSystems[index] = {
          ...newSystems[index],
          business_system_product_language_name: newLanguages.join(', ')
        };
        setValue('business_systems', newSystems, { shouldDirty: true, shouldTouch: true });
      }

      // 入力値をクリア
      setLanguageInputValues(prev => ({
        ...prev,
        [index]: ''
      }));
    }
  };

  // 要素を追加
  const handleAdd = () => {
    const newSystems = [...business_systems, { business_system_name: '', business_system_id: '', business_system_product_language_name: '', business_system_product_language_id: '' }];
    setValue('business_systems', newSystems, { shouldDirty: true, shouldTouch: true });

    // 新しい要素にスクロール
    setTimeout(() => {
      const newIndex = newSystems.length - 1;
      if (formRefs.current[newIndex]) {
        formRefs.current[newIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  // 削除前の確認
  const handleDeleteConfirm = (index) => {
    const system = business_systems[index];
    const hasContent = (system.business_system_name && system.business_system_name !== '') || 
                      (system.business_system_product_language_name && system.business_system_product_language_name !== '');

    if (hasContent) {
      setDeleteConfirmDialog({ open: true, index });
    } else {
      handleDelete(index);
    }
  };

  // 要素を削除
  const handleDelete = (index) => {
    const newSystems = business_systems.filter((_, i) => i !== index);
    setValue(
      'business_systems',
      newSystems.length > 0 ? newSystems : [],
      { shouldDirty: true, shouldTouch: true }
    );
    setDeleteConfirmDialog({ open: false, index: null });

    // 言語の候補はカテゴリー名をキーに保持しているため、行を削除しても消さなくてよい
    // （番号ではなく中身に紐づくので、削除で他の行の候補がズレることもない）
    setLanguageInputValues(prev => {
      const newValues = { ...prev };
      delete newValues[index];
      return newValues;
    });

    // 削除に関連するエラーをクリア
    const businessSystemNameErrorKey = `business_systems.${index}.business_system_name`;
    const languageNameErrorKey = `business_systems.${index}.business_system_product_language_name`;
    clearErrors(businessSystemNameErrorKey);
    clearErrors(languageNameErrorKey);
  };

  // バリデーションエラーの取得
  const getFieldError = (index, fieldName) => {
    const fieldErrors = errors?.business_systems;
    if (showValidation && fieldErrors && fieldErrors[index] && fieldErrors[index][fieldName]) {
      return fieldErrors[index][fieldName];
    }
    return null;
  };

  // バリデーションエラーメッセージの生成
  const validationErrors = [];
  if (showValidation && errors?.business_systems) {
    business_systems.forEach((_, index) => {
      const businessSystemNameError = getFieldError(index, 'business_system_name');
      const languageNameError = getFieldError(index, 'business_system_product_language_name');
      if (businessSystemNameError) validationErrors.push(businessSystemNameError.message);
      if (languageNameError) validationErrors.push(languageNameError.message);
    });
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ marginBottom: '24px' }}>
        業務システム
      </Typography>

      {/* バリデーションエラーメッセージの表示 */}
      {showValidation && validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationErrors.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </Alert>
      )}

      {/* 業務システム項目の一覧 */}
      {business_systems.length > 0 ? (
        business_systems.map((system, index) => {
          const businessSystemNameError = getFieldError(index, 'business_system_name');
          const languageNameError = getFieldError(index, 'business_system_product_language_name');

          return (
            <Box
              key={index}
              ref={el => formRefs.current[index] = el}
              sx={{
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                p: 3,
                mb: 3,
                position: 'relative'
              }}
              data-error={!!(businessSystemNameError || languageNameError)}
            >
              {/* 削除ボタン */}
              <IconButton
                onClick={() => handleDeleteConfirm(index)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  color: '#dc2626',
                  '&:hover': {
                    backgroundColor: '#fef2f2',
                  }
                }}
              >
                <DeleteIcon />
              </IconButton>

              <Grid container spacing={3}>
                {/* カテゴリー */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Typography sx={{ mb: 1 }}>カテゴリー</Typography>
                    <Autocomplete
                      freeSolo
                      options={getFilteredBusinessSystemOptions(index)}
                      getOptionLabel={(option) => typeof option === 'string' ? option : option.name || ''}
                      value={system.business_system_name || ''}
                      onChange={(event, newValue) => handleBusinessSystemChange(index, newValue)}
                      onInputChange={(event, newInputValue) => {
                        // 手動入力時の値を即座に反映
                        if (event && event.type === 'change') {
                          handleBusinessSystemChange(index, newInputValue);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          placeholder="カテゴリーを選択または入力してください"
                          variant="outlined"
                          error={!!businessSystemNameError}
                          helperText={businessSystemNameError?.message}
                          onBlur={(event) => {
                            // フォーカスアウト時に手動入力値を確定
                            const inputValue = event.target.value;
                            if (inputValue && inputValue !== system.business_system_name) {
                              handleBusinessSystemChange(index, inputValue);
                            }
                          }}
                          sx={{
                            '& input:focus': {
                              boxShadow: 'none',
                            },
                          }}
                        />
                      )}
                    />
                  </Box>
                </Grid>

                {/* 言語 */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Typography sx={{ mb: 1 }}>製品・言語（複数選択可）</Typography>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={getFilteredLanguageOptions(index)}
                      getOptionLabel={(option) => typeof option === 'string' ? option : option.name || ''}
                      value={system.business_system_product_language_name ? system.business_system_product_language_name.split(', ').filter(f => f.trim() !== '') : []}
                      inputValue={languageInputValues[index] || ''}
                      onChange={(event, newValue) => handleLanguageChange(index, newValue)}
                      onInputChange={(event, newInputValue) => handleLanguageInputChange(index, newInputValue)}
                      renderTags={(value, getTagProps) =>
                        value.map((option, tagIndex) => (
                          <Chip
                            variant="filled"
                            label={option}
                            {...getTagProps({ index: tagIndex })}
                            key={tagIndex}
                            sx={{
                              backgroundColor: '#4F46E5',
                              color: 'white',
                              '& .MuiChip-deleteIcon': {
                                color: 'white',
                                '&:hover': {
                                  color: '#f0f0f0'
                                }
                              }
                            }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          placeholder="製品・言語を選択または入力してください"
                          variant="outlined"
                          error={!!languageNameError}
                          helperText={languageNameError?.message}
                          onBlur={() => handleLanguageBlur(index)}
                          sx={{
                            '& input:focus': {
                              boxShadow: 'none',
                            },
                          }}
                        />
                      )}
                      sx={{
                        '& .MuiAutocomplete-tag': {
                          margin: '2px',
                        },
                        '& .MuiAutocomplete-inputRoot': {
                          minHeight: '56px',
                          padding: '8px 12px',
                        },
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          );
        })
      ) : (
        // すべてのフォームが削除された場合のメッセージ
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            業務システムの項目を追加してください
          </Typography>
        </Box>
      )}

      {/* 追加ボタン */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Fab
          color="primary"
          onClick={handleAdd}
          sx={{
            bgcolor: '#4F46E5',
            '&:hover': {
              bgcolor: '#4338CA',
            }
          }}
        >
          <AddIcon />
        </Fab>
      </Box>

      {/* 削除確認ダイアログ */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={() => setDeleteConfirmDialog({ open: false, index: null })}
      >
        <DialogTitle>削除の確認</DialogTitle>
        <DialogContent>
          <Typography>
            本当に削除しますか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmDialog({ open: false, index: null })}
            color="primary"
          >
            キャンセル
          </Button>
          <Button
            onClick={() => handleDelete(deleteConfirmDialog.index)}
            color="error"
            variant="contained"
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
